import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Table, Button, Group, Title, Badge, ActionIcon, Card, Text, Box } from '@mantine/core'
import { IconPlus, IconEdit, IconTrash, IconPlayerPlay } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import ConfirmModal from '@/components/ConfirmModal'
import { api, getFileUrl } from '@/lib/api'

export default function LessonList() {
  const { id: courseId } = useParams()
  const navigate = useNavigate()
  const [lessons, setLessons] = useState<Record<string, unknown>[]>([])
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)

  const load = useCallback(() => {
    if (!courseId) return
    api.admin.getLessons(courseId).then(setLessons)
  }, [courseId])

  useEffect(() => { load() }, [load])

  const handleDelete = async () => {
    if (!deleteId) return
    setDeleting(true)
    try {
      await api.admin.deleteLesson(deleteId)
      notifications.show({ title: 'Амжилттай', message: 'Хичээл устгагдлаа', color: 'green' })
      load()
    } catch {
      notifications.show({ title: 'Алдаа', message: 'Устгахад алдаа гарлаа', color: 'red' })
    } finally {
      setDeleting(false)
      setDeleteId(null)
    }
  } 
  const formatDuration = (seconds: number) => {
    if(!seconds || isNaN(seconds)) return '-'
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={2}>Хичээлүүд</Title>
        <Group>
          <Button variant="default" onClick={() => navigate('/courses')}>Буцах</Button>
          <Button leftSection={<IconPlus size={16} />} onClick={() => navigate(`/courses/${courseId}/lessons/new`)}>
            Шинэ хичээл
          </Button>
        </Group>
      </Group>

      <Card shadow="xs" padding={0} radius="md" withBorder>
        <Table.ScrollContainer minWidth={500}>
          <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
            <Table.Thead>
              <Table.Tr style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700} w={50}>#</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Дугаар</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Бичлэг</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Гарчиг</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Хугацаа (мин)</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Үнэгүй</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Зөвхөн гишүүдэд</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Үйлдэл</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {lessons.map((l, i) => (
                <Table.Tr key={l._id as string}>
                  <Table.Td c="dimmed" fz="sm">{i + 1}</Table.Td>
                  <Table.Td fw={500}>{l.order as number}</Table.Td>
                  <Table.Td>
                    {(l.videoUrl as string) ? (
                      <Box
                        pos="relative"
                        w={80}
                        h={50}
                        style={{ borderRadius: 6, overflow: 'hidden', background: '#000' }}
                      >
                        <video
                          src={getFileUrl(l.videoUrl as string)}
                          preload="metadata"
                          muted
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        <Box
                          pos="absolute"
                          top={0}
                          left={0}
                          w="100%"
                          h="100%"
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)' }}
                        >
                          <IconPlayerPlay size={20} color="white" />
                        </Box>
                      </Box>
                    ) : (
                      <Box
                        w={80}
                        h={50}
                        bg="gray.1"
                        style={{ borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        <Text size="xs" c="dimmed">Байхгүй</Text>
                      </Box>
                    )}
                  </Table.Td>
                  <Table.Td fw={500}>{l.title as string}</Table.Td>
                  <Table.Td c="dimmed">{formatDuration(l.duration as number)}</Table.Td>
                  <Table.Td>
                    <Badge variant="light" color={l.isFree ? 'green' : 'gray'}>{l.isFree ? 'Тийм' : 'Үгүй'}</Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" color={l.isMemberOnly ? 'green' : 'gray'}>{l.isMemberOnly ? 'Тийм' : 'Үгүй'}</Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={6}>
                      <ActionIcon variant="light" color="blue" radius="md" onClick={() => navigate(`/courses/${courseId}/lessons/${l._id}/edit`)}>
                        <IconEdit size={16} />
                      </ActionIcon>
                      <ActionIcon variant="light" color="red" radius="md" onClick={() => setDeleteId(l._id as string)}>
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
              {lessons.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <Text ta="center" c="dimmed" py="xl">Хичээл олдсонгүй</Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>

      <ConfirmModal opened={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete} loading={deleting} />
    </div>
  )
}
